import React, { PropsWithChildren } from 'react'
import { concatStyles } from 'utils'

export interface CardViewProps {
  /**
   * Apply a hairline border to the appearance of the card.
   */
  outline?: boolean
  /**
   * Apply a hover effect to the card.
   */
  hover?: boolean
  /**
   * Adjusts the appearance of the card to reflect a darker style.
   */
  internal?: boolean
  /**
   * If true the card will have no background or shadow.
   */
  invisible?: boolean
  /**
   * If true a shadow will be applied regardless of any other style altering flag.
   */
  shadow?: boolean
  /**
   * Adjusts the appearance of the card to reflect no user interaction is possible.
   */
  disabled?: boolean
  /**
   * An optional click handler.
   */
  onClick?: React.MouseEventHandler
  /**
   * Any additional styles to append to the default card.
   */
  className?: string
  /**
   * A label that will be rendered purely for accessibility purposes as it is invisible.
   */
  accessibilityLabel?: string
  /**
   * Any direct style overrides.
   */
  style?: React.CSSProperties
  /**
   * Optional mouse over handler.
   */
  onMouseOver?: React.MouseEventHandler
  /**
   * Optional mouse out handler.
   */
  onMouseOut?: React.MouseEventHandler
}

const BASE_STYLES = 'flex relative rounded-md'
const CARD_STYLES =
  'bg-contentareabackgroundalt shadow-[0_1px_4px_rgba(19,32,72,0.12)] cursor-pointer'
const INTERNAL_CARD_STYLES =
  'bg-contentareabackgroundalt shadow-[0_1px_3px_rgba(19,32,72,0.1)]'
const CARD_HOVER_STYLES =
  'hover:shadow-[0_4px_10px_rgba(19,32,72,0.18)] transition-shadow duration-200'
const CARD_DISABLED_STYLES = 'opacity-50'
const CARD_BORDER_STYLES = 'ring-1 ring-contentareaborder'
const HIDDEN_BUTTON_STYLES =
  'absolute top-0 left-0 h-full w-full opacity-0 z-10 right-0 bottom-0 cursor-pointer'
const DEFAULT_PADDING = 'p-2'
const SHADOW_STYLES = 'shadow'

export const View: React.FC<PropsWithChildren<CardViewProps>> = ({
  children,
  onClick,
  accessibilityLabel,
  onMouseOut,
  onMouseOver,
  hover = true,
  ...p
}) => (
  <div
    className={concatStyles([
      BASE_STYLES,
      !p.internal && !p.invisible ? CARD_STYLES : '',
      p.internal ? INTERNAL_CARD_STYLES : '',
      p.outline ? CARD_BORDER_STYLES : '',
      p.disabled ? CARD_DISABLED_STYLES : '',
      hover && !p.disabled ? CARD_HOVER_STYLES : '',
      p.className ?? '',
      p.shadow ? SHADOW_STYLES : '',
      DEFAULT_PADDING,
    ])}
    style={p.style}
    onMouseOver={onMouseOver}
    onMouseOut={onMouseOut}
  >
    {children}
    {onClick && !p.disabled ? (
      <button
        className={HIDDEN_BUTTON_STYLES}
        onClick={(e) => {
          // Check if click came from a button or icon inside the card
          const target = e.target as HTMLElement
          const clickedInsideButton =
            e.currentTarget !== e.target &&
            (target.closest('button') || target.closest('[role="button"]'))

          // Only trigger the click if not clicking inside another button
          if (!clickedInsideButton) {
            onClick(e)
          }
        }}
      >
        {accessibilityLabel ?? 'click to continue'}
      </button>
    ) : null}
  </div>
)

View.displayName = 'Card.View'
